import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import api from '../services/api';
import PremiumSelect from './PremiumSelect';
import TcPagination from './TcPagination';
import StatusChip, { StatusTone } from './ui/StatusChip';
import { InvoiceSearchPage, InvoiceSubmission, InvoiceSubmissionEvent } from '../types';
import {
  isStaleInvoiceRequest,
  pageAfterEmptyInvoiceResult,
  reaisInputToCents,
} from '../utils/invoiceSearch';

const MONTH_OPTIONS = [
  { value: '', label: 'Todos' },
  ...Array.from({ length: 12 }, (_, index) => ({
    value: String(index + 1),
    label: String(index + 1).padStart(2, '0'),
  })),
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'under_review', label: 'Em análise' },
  { value: 'approved', label: 'Aprovadas' },
  { value: 'rejected', label: 'Rejeitadas' },
];

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  under_review: 'Em análise',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
};

const EVENT_LABEL: Record<InvoiceSubmissionEvent['type'], string> = {
  submitted: 'Envio',
  approved: 'Aprovação',
  rejected: 'Rejeição',
  soft_deleted: 'Exclusão',
};

function formatEventWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function formatStatusChange(event: InvoiceSubmissionEvent): string {
  const from = event.fromStatus ? STATUS_LABEL[event.fromStatus] || event.fromStatus : '—';
  const to = event.toStatus ? STATUS_LABEL[event.toStatus] || event.toStatus : '—';
  return `${from} → ${to}`;
}

function statusTone(status: string): StatusTone {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'danger';
  if (status === 'under_review') return 'info';
  return 'warning';
}

function formatAmount(cents: number | null): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function purposeLabel(purpose?: InvoiceSubmission['purpose']): string | null {
  if (purpose === 'salary') return 'Salário do mês';
  if (purpose === 'other') return 'Nota avulsa';
  return null;
}

function summaryLine(inv: InvoiceSubmission): string {
  const parts = [`${String(inv.referenceMonth).padStart(2, '0')}/${inv.referenceYear}`];
  const purpose = purposeLabel(inv.purpose);
  if (purpose) parts.push(purpose);
  if (inv.invoiceNumber?.trim()) parts.push(`NF ${inv.invoiceNumber.trim()}`);
  parts.push(formatAmount(inv.amountCents));
  return parts.join(' · ');
}

function downloadItems(inv: InvoiceSubmission): Array<{ attachmentId?: string; name: string }> {
  if (inv.attachments?.length) {
    return inv.attachments.map((attachment) => ({
      attachmentId: attachment.id,
      name: attachment.originalName,
    }));
  }
  return [{ name: inv.originalFileName || 'nota-fiscal' }];
}

async function downloadInvoice(
  id: string,
  review: boolean,
  fallbackName: string,
  attachmentId?: string,
): Promise<void> {
  const path = attachmentId
    ? review
      ? `/timeclock/hr/invoices/${id}/files/${attachmentId}`
      : `/timeclock/my/invoices/${id}/files/${attachmentId}`
    : review
      ? `/timeclock/hr/invoices/${id}/file`
      : `/timeclock/my/invoices/${id}/file`;
  const res = await api.get<Blob>(path, { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

type AppliedFilters = {
  year: string;
  month: string;
  status: string;
  userId: string;
  number: string;
  min: string;
  max: string;
  page: number;
  limit: number;
  nonce: number;
};

const INITIAL_FILTERS: AppliedFilters = {
  year: '',
  month: '',
  status: '',
  userId: '',
  number: '',
  min: '',
  max: '',
  page: 1,
  limit: 10,
  nonce: 0,
};

interface InvoiceHistoryPanelProps {
  mode: 'mine' | 'review';
  refreshKey: number;
  collaborators?: Array<{ id: string; name: string }>;
  canReview?: boolean;
  canDelete?: boolean;
  onReview?: (id: string, decision: 'approved' | 'rejected') => void;
  onDelete?: (id: string) => void;
}

const InvoiceHistoryPanel: React.FC<InvoiceHistoryPanelProps> = ({
  mode,
  refreshKey,
  collaborators = [],
  canReview = false,
  canDelete = false,
  onReview,
  onDelete,
}) => {
  const [draftNumber, setDraftNumber] = useState('');
  const [draftMin, setDraftMin] = useState('');
  const [draftMax, setDraftMax] = useState('');
  const [applied, setApplied] = useState<AppliedFilters>(INITIAL_FILTERS);
  const [result, setResult] = useState<InvoiceSearchPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const seenRefresh = useRef(refreshKey);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [historyById, setHistoryById] = useState<Record<string, InvoiceSubmissionEvent[]>>({});
  const [historyLoadingId, setHistoryLoadingId] = useState<string | null>(null);

  const eventsPath = (id: string) =>
    mode === 'review' ? `/timeclock/hr/invoices/${id}/events` : `/timeclock/my/invoices/${id}/events`;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const number = draftNumber.trim();
      const min = draftMin.trim();
      const max = draftMax.trim();
      setApplied((prev) => {
        if (prev.number === number && prev.min === min && prev.max === max) return prev;
        return { ...prev, number, min, max, page: 1 };
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [draftNumber, draftMin, draftMax]);

  useEffect(() => {
    if (seenRefresh.current === refreshKey) return;
    seenRefresh.current = refreshKey;
    setHistoryId(null);
    setHistoryById({});
    setApplied((prev) => ({ ...prev, page: 1, nonce: prev.nonce + 1 }));
  }, [refreshKey]);

  useEffect(() => {
    const minCents = applied.min ? reaisInputToCents(applied.min) : null;
    const maxCents = applied.max ? reaisInputToCents(applied.max) : null;
    if (applied.min && minCents == null) {
      requestSeq.current += 1;
      setLoading(false);
      setError('Valor mínimo inválido. Use reais, como 1500,00.');
      return;
    }
    if (applied.max && maxCents == null) {
      requestSeq.current += 1;
      setLoading(false);
      setError('Valor máximo inválido. Use reais, como 1500,00.');
      return;
    }
    if (minCents != null && maxCents != null && minCents > maxCents) {
      requestSeq.current += 1;
      setLoading(false);
      setError('O valor mínimo não pode ser maior que o máximo.');
      return;
    }
    if (applied.year && !/^\d{4}$/.test(applied.year)) {
      requestSeq.current += 1;
      setLoading(false);
      setError('Ano de competência inválido.');
      return;
    }

    const seq = ++requestSeq.current;
    const params: Record<string, string | number> = { page: applied.page, limit: applied.limit };
    if (applied.year) params.referenceYear = Number(applied.year);
    if (applied.month) params.referenceMonth = Number(applied.month);
    if (applied.status) params.status = applied.status;
    if (applied.number) params.invoiceNumber = applied.number;
    if (mode === 'review' && applied.userId) params.userId = applied.userId;
    if (minCents != null) params.amountMin = minCents;
    if (maxCents != null) params.amountMax = maxCents;

    setLoading(true);
    setError(null);
    const path = mode === 'review' ? '/timeclock/hr/invoices/search' : '/timeclock/my/invoices/search';
    void api
      .get<InvoiceSearchPage>(path, { params })
      .then((res) => {
        if (isStaleInvoiceRequest(seq, requestSeq.current)) return;
        const nextPage = pageAfterEmptyInvoiceResult(
          applied.page,
          res.data.items.length,
          res.data.totalPages,
        );
        if (nextPage != null) {
          setApplied((prev) => (prev.page === applied.page ? { ...prev, page: nextPage } : prev));
          return;
        }
        setResult(res.data);
      })
      .catch(() => {
        if (isStaleInvoiceRequest(seq, requestSeq.current)) return;
        setError('Não foi possível carregar o histórico de notas fiscais.');
      })
      .finally(() => {
        if (!isStaleInvoiceRequest(seq, requestSeq.current)) setLoading(false);
      });
  }, [applied, mode]);

  const items = result?.items ?? [];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <PremiumSelect
            id={`invoice-history-month-${mode}`}
            label="Competência (mês)"
            value={applied.month}
            options={MONTH_OPTIONS}
            onChange={(month) => setApplied((prev) => ({ ...prev, month, page: 1 }))}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            label="Ano"
            fullWidth
            value={applied.year}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setApplied((prev) => ({ ...prev, year: event.target.value, page: 1 }))
            }
            placeholder="Todos"
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <PremiumSelect
            id={`invoice-history-status-${mode}`}
            label="Situação"
            value={applied.status}
            options={STATUS_OPTIONS}
            onChange={(status) => setApplied((prev) => ({ ...prev, status, page: 1 }))}
          />
        </Grid>
        {mode === 'review' ? (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <PremiumSelect
              id="invoice-history-user"
              label="Colaborador"
              value={applied.userId}
              options={[
                { value: '', label: 'Todos' },
                ...collaborators.map((person) => ({ value: person.id, label: person.name })),
              ]}
              onChange={(userId) => setApplied((prev) => ({ ...prev, userId, page: 1 }))}
            />
          </Grid>
        ) : null}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Número da nota"
            fullWidth
            value={draftNumber}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setDraftNumber(event.target.value)}
            placeholder="Buscar"
            slotProps={{ htmlInput: { maxLength: 64 } }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 4 }}>
          <TextField
            label="Valor mín. (R$)"
            fullWidth
            value={draftMin}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setDraftMin(event.target.value)}
            placeholder="0,00"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 4 }}>
          <TextField
            label="Valor máx. (R$)"
            fullWidth
            value={draftMax}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setDraftMax(event.target.value)}
            placeholder="0,00"
          />
        </Grid>
      </Grid>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading && items.length === 0 ? (
        <Typography color="text.secondary">Carregando notas fiscais…</Typography>
      ) : null}

      {!loading && items.length === 0 && !error ? (
        <Typography color="text.secondary">Nenhuma nota fiscal encontrada.</Typography>
      ) : null}

      <Stack spacing={1.5}>
        {items.map((inv) => (
          <Paper key={inv.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              {mode === 'review' ? (
                <Typography component="strong" sx={{ fontWeight: 700 }}>
                  {inv.userName || inv.userId}
                </Typography>
              ) : null}
              <Typography component="strong" sx={{ fontWeight: 700 }}>
                {summaryLine(inv)}
              </Typography>
              <StatusChip label={STATUS_LABEL[inv.status] || inv.status} tone={statusTone(inv.status)} />
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {inv.notes}
            </Typography>
            {inv.rejectReason ? (
              <Typography variant="caption" color="text.secondary">
                Motivo da rejeição: {inv.rejectReason}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {downloadItems(inv).map((item, index, list) => (
                <Button
                  key={item.attachmentId ?? `legacy-${index}`}
                  variant="text"
                  size="small"
                  onClick={() =>
                    void downloadInvoice(inv.id, mode === 'review', item.name, item.attachmentId).catch(() =>
                      setError('Erro ao baixar arquivo.'),
                    )
                  }
                >
                  {list.length > 1 ? `Baixar ${item.name}` : 'Baixar NF'}
                </Button>
              ))}
              {canReview && (inv.status === 'pending' || inv.status === 'under_review') ? (
                <>
                  <Button variant="contained" size="small" onClick={() => onReview?.(inv.id, 'approved')}>
                    Aprovar
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => onReview?.(inv.id, 'rejected')}>
                    Rejeitar
                  </Button>
                </>
              ) : null}
              {canDelete ? (
                <Button variant="text" size="small" color="error" onClick={() => onDelete?.(inv.id)}>
                  Excluir
                </Button>
              ) : null}
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  if (historyId === inv.id) {
                    setHistoryId(null);
                    return;
                  }
                  setHistoryId(inv.id);
                  if (historyById[inv.id]) return;
                  setHistoryLoadingId(inv.id);
                  void api
                    .get<InvoiceSubmissionEvent[]>(eventsPath(inv.id))
                    .then((res) => {
                      setHistoryById((prev) => ({ ...prev, [inv.id]: res.data }));
                    })
                    .catch(() => setError('Erro ao carregar o histórico da nota.'))
                    .finally(() => setHistoryLoadingId((current) => (current === inv.id ? null : current)));
                }}
              >
                Histórico
              </Button>
            </Stack>
            {historyId === inv.id ? (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {historyLoadingId === inv.id ? (
                  <Typography variant="body2" color="text.secondary">
                    Carregando histórico…
                  </Typography>
                ) : null}
                {historyLoadingId !== inv.id && historyById[inv.id]?.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum evento registrado. Notas anteriores à auditoria permanecem sem histórico.
                  </Typography>
                ) : null}
                {(historyById[inv.id] ?? []).map((event) => (
                  <Typography key={event.id} variant="body2">
                    {formatEventWhen(event.createdAt)} · {event.actorName} · {EVENT_LABEL[event.type] || event.type} ·{' '}
                    {formatStatusChange(event)}
                    {event.reason ? ` · ${event.reason}` : ''}
                  </Typography>
                ))}
              </Stack>
            ) : null}
          </Paper>
        ))}
      </Stack>

      <TcPagination
        page={result?.page ?? applied.page}
        totalPages={result?.totalPages ?? 0}
        total={result?.total ?? 0}
        pageSize={applied.limit}
        itemLabel="notas"
        onPageChange={(page) => setApplied((prev) => ({ ...prev, page }))}
        onPageSizeChange={(limit) => setApplied((prev) => ({ ...prev, limit, page: 1 }))}
      />
    </Box>
  );
};

export default InvoiceHistoryPanel;
